import React, { useState, useMemo, useEffect } from 'react'
import { Popup, Search, Cell } from 'react-vant'
import { useTranslation } from 'react-i18next'
import { getCountryList, type CountryOption } from '@/utils/countryData'

interface CountrySelectorProps {
    value: string
    onChange: (value: string, countryId?: number) => void
    placeholder?: string
    options?: Array<{ value: string; label: string }> // 可选，如果不传则使用内置数据
    returnType?: 'label' | 'value' // 返回类型：label（国家名称）或 value（二字码）
    disabled?: boolean // 禁用状态
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
    value,
    onChange,
    placeholder,
    options,
    returnType = 'label', // 默认返回 label
    disabled = false
}) => {
    const { t } = useTranslation()
    const [visible, setVisible] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [countryList, setCountryList] = useState<CountryOption[]>([])

    // 加载国家列表
    useEffect(() => {
        if (!options) {
            getCountryList().then(countries => {
                setCountryList(countries)
            })
        }
    }, [options])

    // 使用传入的 options 或内置的 countryList
    const finalOptions = useMemo(() => {
        return options || countryList
    }, [options, countryList])

    // 获取选中的国家（同时兼容二字码和 label 回显）
    const selectedCountry = useMemo(() => {
        if (!value) return undefined
        // 优先按 returnType 匹配
        if (returnType === 'value') {
            const byValue = finalOptions.find(option => option.value === value)
            if (byValue) return byValue
            // 兜底：尝试用 label 匹配（数据可能是国家名称）
            return finalOptions.find(option => option.label === value)
        } else {
            const byLabel = finalOptions.find(option => option.label === value)
            if (byLabel) return byLabel
            // 兜底：尝试用二字码匹配（数据可能是二字码）
            return finalOptions.find(option => option.value === value)
        }
    }, [finalOptions, value, returnType])

    // 过滤国家列表
    const filteredCountries = useMemo(() => {
        if (!searchText.trim()) {
            return finalOptions
        }
        const searchLower = searchText.toLowerCase()
        return finalOptions.filter(country =>
            country.label.toLowerCase().includes(searchLower) ||
            country.value.toLowerCase().includes(searchLower)
        )
    }, [finalOptions, searchText])

    // 处理选择
    const handleSelect = (country: CountryOption | { value: string; label: string }) => {
        const countryId = 'id' in country ? country.id : undefined
        // 根据 returnType 返回不同的值
        const returnValue = returnType === 'value' ? country.value : country.label
        onChange(returnValue, countryId)
        setVisible(false)
        setSearchText('')
    }

    return (
        <>
            <div
                onClick={disabled ? undefined : () => setVisible(true)}
                className={`w-full px-4 h-12 border-1 border-[#EAECF0] border-solid rounded-2.5 bg-white flex items-center justify-between ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            >
                <span className={`text-base text-[#C9CDD4] ${selectedCountry ? 'text-black' : 'text-[#C9CDD4]/70'}`}>
                    {selectedCountry ? selectedCountry.label : placeholder}
                </span>
                {!disabled && <img src="/icon/select-down.svg" alt="" />}
            </div>

            <Popup
                visible={visible}
                onClose={() => {
                    setVisible(false)
                    setSearchText('')
                }}
                position="bottom"
                round
                style={{ height: '70%' }}
            >
                <div className="flex flex-col h-full">
                    {/* 标题 */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <span
                            className="text-sm text-gray-500 cursor-pointer"
                            onClick={() => {
                                setVisible(false)
                                setSearchText('')
                            }}
                        >
                            {t('取消')}
                        </span>
                        <span className="text-base font-medium">{t('请选择国家或地区')}</span>
                        <span className="w-12"></span>
                    </div>

                    {/* 搜索框 */}
                    <Search
                        value={searchText}
                        onChange={setSearchText}
                        placeholder={t('搜索国家或地区')}
                        shape="round"
                    />

                    {/* 国家列表 */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                                <Cell
                                    key={country.value}
                                    title={country.label}
                                    clickable
                                    onClick={() => handleSelect(country)}
                                    className={selectedCountry?.value === country.value ? 'bg-blue-50' : ''}
                                    rightIcon={selectedCountry?.value === country.value ? <span className="text-blue-600">✓</span> : null}
                                />
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                <div className="text-sm">{t('未找到匹配的国家或地区')}</div>
                            </div>
                        )}
                    </div>
                </div>
            </Popup>
        </>
    )
}

export default CountrySelector
